import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useOnScreen } from 'shared/hooks/useOnScreen'

import { ILoadableImageProps } from '../LoadableImage'

interface IUseLoading {
  src: ILoadableImageProps['src']
  onLoad: ILoadableImageProps['onLoad']
}

export const useLoading = ({ src, onLoad }: IUseLoading) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)
  const { containerRef, isVisible } = useOnScreen()

  const handleLoad = useCallback(() => {
    if (isLoaded) {
      return
    }
    setIsLoaded(true)

    if (imageRef.current) {
      onLoad?.(imageRef.current)
    }
  }, [isLoaded, onLoad])

  useEffect(() => {
    const imageDOM = imageRef.current || document.createElement('img')

    imageDOM.addEventListener('load', handleLoad)
    return () => {
      imageDOM.removeEventListener('load', handleLoad)
    }
  }, [src, handleLoad])

  const currentSrc = useMemo(() => (isVisible || isLoaded ? src : undefined), [isLoaded, isVisible, src])

  return useMemo(
    () => ({
      currentSrc,
      isLoaded,
      imageRef,
      containerRef,
      isVisible,
      setIsLoaded,
    }),
    [currentSrc, isLoaded, imageRef, containerRef, isVisible, setIsLoaded],
  )
}
